import React, { useState, useEffect, ChangeEventHandler } from 'react';
import { MaskedInputProps } from 'react-text-mask';
import {
  PasswordToggler,
  FormInput,
  FormControl,
  Label,
  CheckmarkIcon,
  TextMaskedInput,
  ErrorIcon,
  ErrorMessageContainer,
  TextArea
} from './Input.styles';
import { getInputType, validateInputProps } from './operations';
import { classnames } from "../operations";

export type InputProps = {
  id: string;
  type?: string;
  labelText?: string;
  name?: string;
  isDisabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  value?: string;
  error?: string;
  touched?: boolean;
  onBlur?: ChangeEventHandler<HTMLInputElement>;
  onFocus?: ChangeEventHandler<HTMLInputElement>;
  testHook?: string;
  className?: string;
  errorClassName?: string;
  textMask?: MaskedInputProps['mask'];
  isRequired?: boolean;
  sanitize?: (arg: any) => any;
  minLength?: number;
  maxLength?: number;
  multiline?: boolean;
  autoComplete?: string;
  changeCallback?: (arg: any) => any;
  hideCheckmark?: boolean;
  pattern?: string;
  inputMode?: 'search' | 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'url' | 'email';
};

const Input = ({
  type = 'text',
  id,
  labelText = '',
  name = '',
  isDisabled = false,
  onChange,
  value = '',
  error = '',
  touched = false,
  onBlur,
  onFocus,
  testHook = 'Input',
  className = '',
  errorClassName = '',
  textMask,
  isRequired = false,
  sanitize,
  minLength = 0,
  maxLength = 0,
  multiline = false,
  autoComplete = 'on',
  changeCallback,
  hideCheckmark = false,
  pattern,
  inputMode
}: InputProps) => {
  const checkForValidValue = (input: string) => input != null && input !== '';
  const hasValueProp = checkForValidValue(value);
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [hasValue, setHasValue] = useState(hasValueProp);
  const [isFocused, setIsFocused] = useState(false);

  const isPasswordInput = type === 'password';
  const inputType = getInputType(type, showPassword);
  const isValid = !error && touched && hasValueProp;
  const shouldShowError = error && touched;
  const shouldShowCheckmark = !hideCheckmark && isValid;
  const noop = f => f;

  useEffect(() => validateInputProps({id, onChange, changeCallback, error, sanitize}), []);

  useEffect(() => setHasValue(checkForValidValue(value)), [value]);

  useEffect(() => setInputValue(value || ''), [value]);

  const togglePasswordHiddenState = () => setShowPassword(!showPassword);

  const classNames = classnames(
    {
      'show-error-icon': shouldShowError,
      'is-populated': hasValue || hasValueProp,
      'is-disabled': isDisabled,
      'is-focused': isFocused
    },
    [className]
  );

  const onInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const sanitizeFn = sanitize || noop;
    const newValue = sanitizeFn(e.target.value);
    setInputValue(newValue);
    setHasValue(checkForValidValue(newValue));
    if (changeCallback) changeCallback(newValue);
  };

  const onInputBlur = e => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const onInputFocus = e => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleAutofill = e => {
    if (e.animationName === 'onAutoFillStart') {
      setHasValue(true);
    }
  };

  const inputProps = {
    value: onChange ? value : inputValue,
    disabled: isDisabled,
    id,
    type: inputType,
    onBlur: onInputBlur,
    onChange: onChange || onInputChange,
    onFocus: onInputFocus,
    name,
    required: isRequired,
    ...(minLength && { minLength }),
    ...(maxLength && { maxLength }),
    autoComplete,
    onAnimationStart: handleAutofill,
    'aria-labelledby': labelText || id,
    pattern,
    inputMode
  };

  const renderNonTextMaskInput = (isMultiline, passedProps) =>
    isMultiline ? <TextArea {...passedProps} /> : <FormInput {...passedProps} />;

  return (
    <>
      <FormControl className={classNames} data-cv-test={testHook} multiline={multiline}>
        <Label htmlFor={id}>{labelText}</Label>
        {textMask && <TextMaskedInput {...inputProps} mask={textMask} />}
        {!textMask && renderNonTextMaskInput(multiline, inputProps)}
        {shouldShowCheckmark && <CheckmarkIcon className="input-validated-icon" height={15} width={15} />}
        {shouldShowError && <ErrorIcon className="input-error-icon" height={15} width={15} />}
        {isPasswordInput && (
          <PasswordToggler onClick={togglePasswordHiddenState}>{showPassword ? 'Hide' : 'Show'}</PasswordToggler>
        )}
      </FormControl>
      {shouldShowError && <ErrorMessageContainer className={errorClassName}>{error}</ErrorMessageContainer>}
    </>
  );
};

export default Input;
